import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Users, Coins } from "lucide-react";
import api from "../lib/api";

/**
 * LiveTournamentChip — small persistent banner on /levels showing
 * the 1-2 most recently created OPEN tournaments. Clicks deep-link to
 * `/tournaments/{id}` for the bracket detail page.
 *
 * Auto-refreshes every 30 seconds. Hidden when there are no open brackets.
 */
export default function LiveTournamentChip() {
  const [open, setOpen] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const fetch = () => {
      api.get("/tournaments/open")
        .then((r) => {
          if (cancelled) return;
          // Most recent first; only show those with empty seats.
          const rows = (r.data.tournaments || []).filter(
            (t) => t.participants.length < t.size
          );
          setOpen(rows.slice(0, 2));
        })
        .catch(() => {});
    };
    fetch();
    const t = setInterval(fetch, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (open.length === 0) return null;

  return (
    <div
      className="mb-6 border border-[#ffd700]/30 bg-gradient-to-r from-[#ffd700]/8 to-[#ff007f]/8 p-3"
      data-testid="live-tournament-chip"
    >
      <div className="flex items-center gap-2 mb-2">
        <Trophy className="w-3.5 h-3.5 text-[#ffd700]" />
        <span className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-[#ffd700]">
          LIVE BRACKETS · NEAR YOU
        </span>
        <span className="ml-auto text-[9px] text-white/35 font-mono-cosmic tracking-[0.2em] uppercase">
          {open.length} open
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {open.map((t) => {
          const seats = t.size - t.participants.length;
          return (
            <Link
              key={t.id}
              to={`/tournaments/${t.id}`}
              className="border border-white/15 hover:border-[#ffd700]/50 hover:bg-black/40 bg-black/20 px-3 py-2 transition flex items-center gap-2"
              data-testid={`live-tournament-${t.id}`}
            >
              <span className="font-heading text-sm tabular-nums text-white">
                LVL {t.level}
              </span>
              <span className="flex items-center gap-1 text-[#ffd700] font-mono-cosmic text-[10px] tabular-nums">
                <Coins className="w-3 h-3" />
                {t.buyin} ☾
              </span>
              <span className="flex items-center gap-1 text-white/60 font-mono-cosmic text-[10px] tabular-nums">
                <Users className="w-3 h-3" />
                {t.participants.length}/{t.size}
              </span>
              <span className="ml-auto font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-[#ff007f]">
                {seats} SEAT{seats === 1 ? "" : "S"}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
